import { h as __h } from "preact";
import { signal } from "@preact/signals";
import { Component, createRef, h } from "preact";
/** @import { RefObject } from "preact"; */

import { bulkEnterpriseInviteResponse, inviteResponse } from "~/backend/validate.js";
import { ApiError, query, authedApiRequest, paginatedResponse, response } from "~/lib/api.js";
import { SearchParams } from "~/lib/search.js";
import { alert } from "~/util/alert.js";
import html from "~/util/html.js";
import { pluralize } from "~/util/pluralize.js";
import { PromiseSignal } from "~/util/promise.js";
import { array, object, parse, string } from "~/util/validate.js";
import Button from "~/component/Button.js";
import Data from "~/component/Data.js";
import Field from "~/component/Field.js";
import Icon from "~/component/Icon.js";
import Modal from "~/component/Modal.js";
import Time, { relative } from "~/component/Time.js";
import { Crumb } from "~/admin/component/Breadcrumbs.js";
import { ComboboxInput } from "~/admin/component/Combobox.js";
import Pagination from "~/admin/component/Pagination.js";
import Table from "~/admin/component/Table.js";
import adminStyle from "~/admin/lib/style.js";
/** @import { sessionEnterpriseResponse } from "~/backend/validate.js"; */

import input from "~/style/input.js";
import util from "~/style/util.js";
import { adopt, importCss, CSS_MODULE_SCRIPTS } from "~/util/css.js";
const {
  default: sheet
} = CSS_MODULE_SCRIPTS ? await import("./Invites.module.css", {
  with: {
    type: "css"
  }
}) : await importCss("./Invites.module.css", import.meta.url);
const css = adopt(sheet);

/**
 * @extends {Component<Props>}
 *
 * @typedef {object} Props
 * @property {sessionEnterpriseResponse} enterprise
 */
export default class Invites extends Component {
  checked = signal(/** @type {Map<string, inviteResponse>} */new Map());
  paginate = new Pagination.State();
  sort = new Table.Sort({
    keys: ["updated_at", "expires_at", "email"],
    by: "updated_at",
    order: "desc"
  });
  invites = query(() => ({
    key: [`/v2/enterprises/${this.props.enterprise.id}/invites`, {
      ...this.paginate.query(),
      ...this.sort.query(),
      q: SearchParams.q.value
    }],
    enabled: Boolean(this.props.enterprise),
    keepPreviousData: true
  }), {
    schema: paginatedResponse(inviteResponse)
  });
  render() {
    return [__h(Crumb, {}, "Invites"), __h("div", {
      "class": adminStyle.main
    }, __h(ComboboxInput, {
      query: SearchParams.q,
      debounce: 200
    }), __h("div", {
      "class": adminStyle.body
    }, __h(InvitesActions, {
      enterpriseId: this.props.enterprise.id,
      checked: this.checked,
      invites: this.invites
    }), __h("div", {
      "class": util.card + " " + adminStyle.tableCard
    }, __h(InvitesBody, {
      enterpriseId: this.props.enterprise.id,
      checked: this.checked,
      paginate: this.paginate,
      sort: this.sort,
      invites: this.invites
    }))))];
  }
}

/**
 * @extends {Component<InvitesBodyProps>}
 *
 * @typedef {object} InvitesBodyProps
 * @property {string} enterpriseId
 * @property {typeof Invites.prototype.invites} invites
 * @property {typeof Invites.prototype.checked} checked
 * @property {typeof Invites.prototype.paginate} paginate
 * @property {typeof Invites.prototype.sort} sort
 */
class InvitesBody extends Component {
  render() {
    const invites = this.props.invites;
    const rows = invites.data?.data.map(invite => {
      return {
        key: invite.id,
        label: `invite to ${invite.email}`,
        value: invite,
        columns: {
          user: __h("div", {
            "class": css.noAccountInvite
          }, __h("span", {
            "class": css.sub
          }, "not registered"), __h("span", {}, invite.email)),
          expiry: __h("div", {
            "class": css.expiry,
            "data-expired": invite.is_expired
          }, invite.expires_at ? invite.is_expired ? ["Expired", __h(Time, {
            fmt: relative,
            datetime: new Date(invite.expires_at)
          })] : ["Expires", __h(Time, {
            fmt: relative,
            datetime: new Date(invite.expires_at)
          })] : invite.is_expired ? "Expired" : "Never"),
          sentDate: __h(Time, {
            fmt: relative,
            datetime: new Date(invite.updated_at)
          })
        }
      };
    });
    return h(Data, {
      loading: invites.loading && !invites.data,
      error: invites.error,
      children: h(/** @type {typeof Table<inviteResponse>} */Table, {
        label: "invite",
        checked: this.props.checked.value,
        onCheck: check => this.props.checked.value = check,
        headers: [{
          id: "user",
          label: "User",
          sort: "email"
        }, {
          id: "expiry",
          label: "Expiry",
          sort: "expires_at"
        }, {
          id: "sentDate",
          label: "Sent date",
          sort: "updated_at"
        }],
        empty: SearchParams.q.value ? `No invites match "${SearchParams.q.value}".` : "No invites have been sent.",
        rows: rows ?? [],
        paginate: this.props.paginate.props(invites),
        sort: this.props.sort.props()
      })
    });
  }
}

/**
 * @extends {Component<InvitesActionsProps>}
 *
 * @typedef {object} InvitesActionsProps
 * @property {string} enterpriseId
 * @property {typeof Invites.prototype.invites} invites
 * @property {typeof Invites.prototype.checked} checked
 */
class InvitesActions extends Component {
  /** @type {RefObject<Modal>} */
  newInvitesModalRef = createRef();
  /** @param {string[]} emails */
  async newInvitesModalSubmit(emails) {
    const {
      data
    } = await authedApiRequest("POST", `/v2/enterprises/${this.props.enterpriseId}/invites/bulk`, {
      json: {
        emails
      },
      schema: response(bulkEnterpriseInviteResponse)
    });
    const len = data.invited.length;
    if (len) alert(`Invited ${len} ${pluralize(len, "member")}`, {
      type: "success"
    });
    data.rejected.forEach(reject => {
      alert(`Could not invite ${reject.email}: ${reject.reason}`, {
        type: "error",
        ms: Infinity
      });
    });
    this.props.invites.fetch();
    this.newInvitesModalRef.current?.close();
  }

  /** @type {RefObject<Modal>} */
  withdrawInvitesModalRef = createRef();
  async withdrawInvitesModalSubmit() {
    await authedApiRequest("DELETE", `/v2/enterprises/${this.props.enterpriseId}/invites`, {
      json: {
        invite_ids: [...this.props.checked.value.keys()]
      }
    });
    this.props.invites.fetch();
    this.props.checked.value = new Map();
    this.withdrawInvitesModalRef.current?.close();
  }
  async resendInvites() {
    try {
      const {
        data
      } = await authedApiRequest("POST", `/v2/enterprises/${this.props.enterpriseId}/invites/resend`, {
        json: {
          invite_ids: [...this.props.checked.value.keys()]
        },
        schema: response(array(inviteResponse))
      });
      const len = data.length;
      alert(`Resent ${len} ${pluralize(len, "invite")}`, {
        type: "success"
      });
      this.props.invites.fetch();
      this.props.checked.value = new Map();
    } catch (err) {
      alert(ApiError.from(err).message, {
        type: "error"
      });
    }
  }
  render() {
    const checked = this.props.checked.value;
    const bulk = checked.size > 0;
    return [__h("menu", {
      "class": adminStyle.menu
    }, __h(Button, {
      level: "primary",
      size: "small",
      onClick: () => this.newInvitesModalRef.current?.open()
    }, __h(Icon, {
      name: "plus",
      size: 8
    }), __h("span", {}, "Invite members")), __h(Button, {
      size: "small",
      disabled: !bulk,
      onClick: () => this.resendInvites()
    }, " Resend "), __h(Button, {
      size: "small",
      kind: "negative",
      disabled: !bulk,
      onClick: () => this.withdrawInvitesModalRef.current?.open()
    }, "Withdraw")), __h(NewInvitesModal, {
      modalRef: this.newInvitesModalRef,
      onSubmit: this.newInvitesModalSubmit.bind(this)
    }), __h(WithdrawInvitesModal, {
      modalRef: this.withdrawInvitesModalRef,
      onSubmit: this.withdrawInvitesModalSubmit.bind(this),
      title: "Remove " + checked.size + " " + pluralize(checked.size, "invite") + "?"
    })];
  }
}

/**
 * @extends Component<NewInvitesModalProps>
 *
 * @typedef {object} NewInvitesModalProps
 * @property {(emails: string[]) => Promise<void>} onSubmit
 * @property {RefObject<Modal>} modalRef
 */
class NewInvitesModal extends Component {
  formState = new PromiseSignal(/** @param {SubmitEvent & { currentTarget: HTMLFormElement }} ev */async ev => {
    const form = new FormData(ev.currentTarget);
    const data = parse(object({
      emails: string
    }), Object.fromEntries(form.entries()));
    const emails = data.emails.split(/[,\s]+/).filter(email => email.length);
    if (emails.length) await this.props.onSubmit(emails);
  });
  render() {
    const error = this.formState.error && ApiError.from(this.formState.error);
    return __h(Modal, {
      ref: this.props.modalRef,
      title: "Invite members"
    }, __h("form", {
      "class": adminStyle.modal,
      onSubmit: (/** @type {SubmitEvent & { currentTarget: HTMLFormElement }} */ev) => {
        if (ev.submitter?.getAttribute("formmethod") === "dialog") return;
        ev.preventDefault();
        this.formState.invoke(ev);
      }
    }, __h("div", {}, __h("p", {}, "Enter the email addresses of people to invite to your organization."), __h("p", {}, "You can seperate emails by new lines, spaces, or commas.")), __h(Field, {
      "for": "emails",
      label: "Emails",
      error: error?.fields.emails?.message
    }, __h("textarea", {
      rows: 6,
      "class": input.input,
      id: "emails",
      name: "emails"
    })), __h("p", {
      "class": adminStyle.error
    }, error?.message), __h("div", {
      "class": adminStyle.actions
    }, __h(Button, {
      type: "submit",
      level: "primary",
      loading: this.formState.loading
    }, "Send invites"), __h(Button, {
      type: "submit",
      formmethod: "dialog",
      formnovalidate: true,
      value: "cancel"
    }, " Cancel "))));
  }
}

/**
 * @extends {Component<WithdrawInvitesModalProps>}
 *
 * @typedef {object} WithdrawInvitesModalProps
 * @property {RefObject<Modal>} modalRef
 * @property {() => Promise<void>} onSubmit
 * @property {string} title
 */
class WithdrawInvitesModal extends Component {
  withdrawInvites = new PromiseSignal(/** @param {SubmitEvent} _ev */
  async _ev => {
    await this.props.onSubmit();
  });
  render() {
    const error = this.withdrawInvites.error && ApiError.from(this.withdrawInvites.error);
    return __h(Modal, {
      ref: this.props.modalRef,
      title: this.props.title
    }, __h("div", {
      "class": adminStyle.modal
    }, __h("p", {}, "You can always re-invite people via their email."), __h("p", {
      "class": adminStyle.error
    }, error?.message), __h("form", {
      "class": adminStyle.actions,
      onSubmit: (/** @type {SubmitEvent} */ev) => {
        if (ev.submitter?.getAttribute("formmethod") === "dialog") return;
        ev.preventDefault();
        this.withdrawInvites.invoke(ev);
      }
    }, __h(Button, {
      type: "submit",
      level: "primary",
      kind: "negative",
      value: "delete",
      loading: this.withdrawInvites.loading
    }, "Delete"), __h(Button, {
      type: "submit",
      formmethod: "dialog",
      disabled: this.withdrawInvites.loading
    }, "Cancel"))));
  }
}